import type { Season } from '@features/seasonal-theme/types';

import { useState, useEffect } from 'react';

import { findSeason, resolveSeason } from '@features/seasonal-theme/utils/resolve-season';
import { useSeasonPreference } from '@features/seasonal-theme/hooks/use-season-preference';

// The app stays open for whole shifts: re-check the date so the palette switches at midnight.
const REFRESH_INTERVAL_MS = 60 * 60 * 1000;

const useToday = () => {
  const [today, setToday] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setToday(new Date()), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return today;
};

type ActiveSeason = {
  /** Season applied to the theme, given the user's preference. */
  season: Season | null;
  /** Season matching today's date, whatever the preference. */
  datedSeason: Season | null;
};

export const useActiveSeason = (): ActiveSeason => {
  const { preference } = useSeasonPreference();
  const datedSeason = resolveSeason(useToday());

  if (preference === 'off') {
    return { season: null, datedSeason };
  }

  return { season: preference === 'auto' ? datedSeason : (findSeason(preference) ?? null), datedSeason };
};
